import { useState } from 'react'
import { Chip } from './Chip.js'
import { Input } from './Input.js'
import { Stack, Row } from '../layout/Stack.js'
import { Text } from './Typography.js'

export interface TagInputProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  /** Refuses to add beyond this. The contracts cap most of these at 8 or 20. */
  max?: number
  /** Under the control, saying what these are for. */
  hint?: string
  id?: string
  disabled?: boolean
  /**
   * The rule under the control, so a screen reader reads it with the box.
   *
   * `Field` mints this and hands it to its child, and sixteen call sites on
   * the desk took the `id` and dropped it — so *"One point per line"*,
   * *"At least twelve characters"* and *"Nothing means it is free"* were
   * drawn for the eye and for nobody else.
   */
  'aria-describedby'?: string
}

/**
 * A list somebody types, one item at a time.
 *
 * Thirty-two request fields are arrays of strings — a question's options, a
 * class's learn points, the words a professional is searched by, the languages
 * they work in. Every one of them is this control, and without it each becomes a
 * comma-separated text box and somebody's name with a comma in it becomes two
 * people.
 *
 * Enter adds. Backspace on an empty box removes the last one, which is the thing
 * everybody tries.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  max,
  hint,
  id,
  disabled,
  ...aria
}: TagInputProps) {
  const [draft, setDraft] = useState('')
  const full = max !== undefined && value.length >= max

  const add = () => {
    const text = draft.trim()
    if (!text || full) return
    // Silently ignoring a duplicate is kinder than an error for something the
    // person can see is already there.
    if (!value.includes(text)) onChange([...value, text])
    setDraft('')
  }

  return (
    <Stack gap={2}>
      {value.length > 0 && (
        <Row wrap gap={2}>
          {value.map((t) => (
            <Chip
              key={t}
              onRemove={disabled ? undefined : () => onChange(value.filter((x) => x !== t))}
            >
              {t}
            </Chip>
          ))}
        </Row>
      )}
      <Input
        id={id}
        {...aria}
        disabled={disabled || full}
        value={draft}
        placeholder={full ? `That is the most (${max})` : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1))
          }
        }}
        onBlur={add}
      />
      {(hint || max !== undefined) && (
        <Text variant="hint">
          {hint}
          {hint && max !== undefined ? ' · ' : ''}
          {max !== undefined ? `${value.length} of ${max}` : ''}
        </Text>
      )}
    </Stack>
  )
}
